// modules/freshcards/src/EmbeddingAPIv2.ts
//
// 2026-09-03 (Phase 13) — Embedding API v2.
//
// Stable protocol v2 for mounting FreshCards runtime inside an
// external app. v1 was the embed-bundle JSON from WhiteLabelEmbeddingAPI.
// v2 introduces:
//   - adapter bundle: the host app declares the adapters it needs.
//   - mountRuntime: a function that takes a DOM container + a
//     bundle and renders FreshCards inside the host.
//   - export/import a complete runtime config (adapters + theme +
//     layout + ai preset + page bindings).
//
// PURE module — the actual mount() relies on a host-supplied
// renderer function. The bundle is a JSON document the host can
// store in a database, ship to a CDN, or attach to a deployment.

import type { DesignTokens } from '../adapters/ThemeAdapter';
import type { BackendConnection, DataModelAdapterConfig } from '../adapters/DataModelAdapter';
import { registerExternalBlock, type ExternalBlockConfig } from '../adapters/BlockRegistryAdapter';
import type { ExternalGraph } from '../adapters/SemanticAdapter';
import type { GridDescriptor, SectionDescriptor } from '../adapters/LayoutAdapter';
import { externalTokensToTheme, cssVarsToDesignTokens } from '../adapters/ThemeAdapter';
import { gridToLayout, sectionsToBlockGroups } from '../adapters/LayoutAdapter';
import type { Theme } from './ThemeAPI';
import type { Layout } from './LayoutAPI';

export const EMBED_V2_SCHEMA = 2;

// ---------------------------------------------------------------------------
// Bundle shape
// ---------------------------------------------------------------------------

export interface EmbeddingBundleV2 {
  schemaVersion: typeof EMBED_V2_SCHEMA;
  id: string;
  name: string;
  createdAt: string;
  /** Adapters the host app needs. */
  adapters: AdapterBundle;
  /** Theme + layout. */
  theme: Theme;
  layout: Layout;
  /** Where the runtime mounts. */
  mount: MountSpec;
  /** Optional AI preset. */
  aiPreset?: AIEmbeddingPreset;
  /** Page bindings — optional. */
  pageBindings?: PageBinding[];
  meta?: Record<string, any>;
}

export interface MountSpec {
  /** CSS selector or DOM element id. */
  container: string;
  /** Initial route inside FreshCards. */
  initialRoute?: string;
  /** When true, the host supplies a renderer function; when false,
   *  FreshCards falls back to its built-in surface. */
  useHostRenderer?: boolean;
}

export interface PageBinding {
  /** External app record id. */
  recordId: string;
  /** FreshCards page id (created on import). */
  pageId: string;
  /** What to do when the external record changes. */
  syncMode: 'one-way' | 'two-way' | 'mirror';
}

// ---------------------------------------------------------------------------
// Adapter bundle
// ---------------------------------------------------------------------------

export interface AdapterBundle {
  blockRegistry: ExternalBlockConfig[];
  dataModel: {
    backend: BackendConnection;
    enabledKinds: string[];
    kindToPageKind: Record<string, 'page' | 'database' | 'item'>;
    blockRenderMode: 'reference' | 'embed' | 'mirror';
  } | null;
  semantic: ExternalGraph | null;
  theme: { source: string; tokens: DesignTokens } | null;
  layout: { kind: 'grid'; grid: GridDescriptor } | { kind: 'sections'; sections: SectionDescriptor[] } | null;
}

// ---------------------------------------------------------------------------
// AI preset
// ---------------------------------------------------------------------------

export interface AIEmbeddingPreset {
  id: string;
  name: string;
  styleHint: string;
  preferredBlockTypes: string[];
  preferredSemanticRelations: string[];
}

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

export interface EmbedRuntimeInput {
  name: string;
  adapters: AdapterBundle;
  mount: MountSpec;
  /** Optional overrides. */
  theme?: Theme;
  layout?: Layout;
  aiPreset?: AIEmbeddingPreset;
  pageBindings?: PageBinding[];
  meta?: Record<string, any>;
}

export function embedRuntime(input: EmbedRuntimeInput): EmbeddingBundleV2 {
  // Derive theme from tokens if no override
  const theme = input.theme
    ?? (input.adapters.theme ? externalTokensToTheme(input.adapters.theme.tokens, {
      name: `${input.name} (from ${input.adapters.theme.source})`,
    }) : undefined as any);
  // Derive layout from grid/sections if no override
  const layout = input.layout
    ?? (input.adapters.layout?.kind === 'grid' ? gridToLayout(input.adapters.layout.grid)
      : DEFAULT_LAYOUT_FALLBACK)
    ?? DEFAULT_LAYOUT_FALLBACK;
  return {
    schemaVersion: EMBED_V2_SCHEMA,
    id: `emb-${Date.now().toString(36)}`,
    name: input.name,
    createdAt: new Date().toISOString(),
    adapters: input.adapters,
    theme,
    layout,
    mount: input.mount,
    aiPreset: input.aiPreset,
    pageBindings: input.pageBindings,
    meta: input.meta,
  };
}

const DEFAULT_LAYOUT_FALLBACK: Layout = {
  pageWidth: 'standard',
  sidebar: 'left',
  pageHeader: 'standard',
  spacing: 'standard',
  density: 'comfortable',
  geometry: { radius: 8, shadow: 'sm', borderWidth: 1, padding: 12 },
  typography: { fontFamily: '-apple-system, sans-serif', baseFontSize: 14 },
};

// ---------------------------------------------------------------------------
// Mount — host-supplied renderer
// ---------------------------------------------------------------------------

export interface HostRenderer {
  /** Render FreshCards content inside the given container. */
  mount: (container: HTMLElement, bundle: EmbeddingBundleV2) => void;
  /** Unmount and clean up. */
  unmount: (container: HTMLElement) => void;
}

export function mountRuntime(bundle: EmbeddingBundleV2, renderer: HostRenderer): { ok: boolean; error?: string } {
  if (typeof document === 'undefined') return { ok: false, error: 'no document' };
  if (bundle.schemaVersion !== EMBED_V2_SCHEMA) {
    return { ok: false, error: `unsupported schema: ${bundle.schemaVersion}` };
  }
  const container = document.querySelector(bundle.mount.container) as HTMLElement | null;
  if (!container) return { ok: false, error: `container not found: ${bundle.mount.container}` };
  // Register blocks before mount
  for (const cfg of bundle.adapters.blockRegistry) {
    registerExternalBlock(cfg);
  }
  renderer.mount(container, bundle);
  return { ok: true };
}

// ---------------------------------------------------------------------------
// Export / import
// ---------------------------------------------------------------------------

export function exportEmbeddingBundle(bundle: EmbeddingBundleV2): string {
  return JSON.stringify(bundle, null, 2);
}

export interface ImportResult {
  ok: boolean;
  bundle?: EmbeddingBundleV2;
  error?: string;
}

export function importEmbeddingBundle(json: string): ImportResult {
  try {
    const parsed = JSON.parse(json) as EmbeddingBundleV2;
    if (parsed.schemaVersion !== EMBED_V2_SCHEMA) {
      return { ok: false, error: `unsupported schema: ${parsed.schemaVersion}` };
    }
    if (!parsed.adapters || !parsed.mount) {
      return { ok: false, error: 'missing required fields' };
    }
    return { ok: true, bundle: parsed };
  } catch (e) {
    return { ok: false, error: String((e as Error).message ?? e) };
  }
}

// ---------------------------------------------------------------------------
// HTML snippet — wraps a bundle as a script tag the host can drop
// into its HTML. The script reads the bundle and calls the host's
// FreshCards runtime.
// ---------------------------------------------------------------------------

export function embedScriptHtml(bundle: EmbeddingBundleV2, opts?: { hostOrigin?: string }): string {
  const json = exportEmbeddingBundle(bundle);
  const origin = opts?.hostOrigin ?? '';
  return `<script type="application/json" data-fc-bundle-v2="${bundle.id}">${escapeJson(json)}</script>
<script>(function(){var o=${origin ? JSON.stringify(origin) : '""'};window.__fcBundleV2=window.__fcBundleV2||{};window.__fcBundleV2[${JSON.stringify(bundle.id)}]=${json};})();</script>`;
}

function escapeJson(json: string): string {
  return json.replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026');
}

// ---------------------------------------------------------------------------
// Sync from host — given a host document, capture live CSS vars
// and convert to design tokens, then build a bundle that respects
// the host's theme.
// ---------------------------------------------------------------------------

export function syncFromHost(doc: Document | null | undefined, hostName: string, mountContainer: string): EmbeddingBundleV2 {
  let tokens: DesignTokens;
  // Try to read live CSS vars only when getComputedStyle exists
  // (i.e. we're in a real browser). In test/SSR envs, fall back.
  const canReadCss = typeof doc !== 'undefined'
    && doc !== null
    && typeof (doc as any).documentElement !== 'undefined'
    && (doc as any).documentElement !== null
    && typeof (globalThis as any).getComputedStyle === 'function';
  if (canReadCss) {
    tokens = cssVarsToDesignTokens(doc as Document, hostName);
  } else {
    tokens = {
      source: hostName,
      colors: {},
      typography: { baseFontSize: 14 },
      spacing: { radius: 6 },
    };
  }
  return embedRuntime({
    name: hostName,
    adapters: {
      blockRegistry: [],
      dataModel: null,
      semantic: null,
      theme: { source: hostName, tokens },
      layout: null,
    },
    mount: { container: mountContainer, useHostRenderer: false },
    meta: { syncedFromHost: true },
  });
}

// ---------------------------------------------------------------------------
// Sections helper
// ---------------------------------------------------------------------------

export function withSections(bundle: EmbeddingBundleV2, sections: SectionDescriptor[]): EmbeddingBundleV2 {
  return {
    ...bundle,
    adapters: {
      ...bundle.adapters,
      layout: { kind: 'sections', sections },
    },
  };
}
